/**
 * Money arithmetic, rounding and formatting. The single source of truth
 * (`.docs/00-index.md` §5): no `Math.round` on an amount exists anywhere else, and
 * ESLint enforces that — this file and `dates.ts` are the only two exempt from the
 * rounding rule.
 *
 * Amounts are integer minor units in a JS `number` (`.docs/00-index.md` §6). Every
 * constructor asserts `Number.isSafeInteger`, so a float that got in through a
 * division shows up at the point it was made rather than three invoices later.
 *
 * Rounding is **half away from zero** everywhere. `PRICING.md` §Guardrails: "Round:
 * monthly and project to $10, extras to $5, so tier prices look like prices."
 * Intermediate steps are exact to the minor unit; the $10/$5 rounding happens once,
 * at the end, in the billing currency (`.docs/00-index.md` §6).
 */
import { DomainError } from './errors.js';
import { formatWithMinusSign, numberFormat } from './intl.js';
/** `PRICING.md`: "All base amounts are USD… EUR invoices use an internal rate." */
export const CURRENCIES = ['USD', 'EUR'];
/** $10 / €10 in minor units — the monthly and project rounding unit. */
export const ROUND10_UNIT = 1000;
/** $5 / €5 in minor units — the extras rounding unit. */
export const ROUND5_UNIT = 500;
/** Basis points in one whole. A tier multiplier of 0.7 is 7,000 bps. */
export const BPS_PER_WHOLE = 10_000;
/** Minor units in one major unit. Both currencies here have two decimal places. */
const MINOR_UNITS = 100;
/** The month lengths `prorateByDays` will accept, so a bad denominator is loud. */
const DAYS_IN_A_MONTH = [28, 29, 30, 31];
/* -------------------------------------------------------------- integer rounding */
/**
 * `numerator / denominator`, rounded half away from zero, in exact integer
 * arithmetic. `denominator` is a positive integer; `numerator` may be negative.
 *
 * Exported for the one caller outside this file that has an integer division and
 * no `Money` to round yet: `readmodels/analytics-effective-rate.ts`'s "$/hour" is
 * `recognised minor units × 60 ÷ minutes`, which — unlike `round10`/`round5`/
 * `roundToUnit` — is not a money figure being rounded to a coarser unit, it is a
 * plain division that has to land on a whole minor unit before it can become one.
 * Integer arithmetic here rather than `Math.round` on the floating-point quotient,
 * for the reason every other rounding in this file avoids one: `104125.99999998`
 * is not `104126` by inspection, and this function is exact.
 */
export function divideRoundHalfUp(numerator, denominator) {
    const negative = numerator < 0;
    const magnitude = negative ? -numerator : numerator;
    const remainder = magnitude % denominator;
    const quotient = (magnitude - remainder) / denominator;
    const rounded = remainder * 2 >= denominator ? quotient + 1 : quotient;
    return negative ? -rounded : rounded;
}
/** A possibly fractional value to the nearest integer, half away from zero. */
function roundHalfUp(value) {
    const negative = value < 0;
    const magnitude = negative ? -value : value;
    const fraction = magnitude % 1;
    const whole = magnitude - fraction;
    const rounded = fraction >= 0.5 ? whole + 1 : whole;
    return negative ? -rounded : rounded;
}
function assertSafeInteger(value, detail, meta) {
    if (!Number.isSafeInteger(value)) {
        throw new DomainError('ValidationFailed', detail, meta);
    }
}
function assertCurrency(currency) {
    if (!CURRENCIES.includes(currency)) {
        throw new DomainError('ValidationFailed', `"${currency}" is not a currency we bill in.`, {
            currency,
        });
    }
}
/* ------------------------------------------------------------------ constructors */
/** Builds an amount, refusing anything that is not an integer of minor units. */
export function money(amount, currency) {
    assertCurrency(currency);
    assertSafeInteger(amount, 'An amount is a whole number of minor units — 318000 is $3,180.00.', {
        amount: String(amount),
        currency,
    });
    // Negating, rounding and splitting can all land on -0. It compares equal to 0 but
    // `Object.is`, a deep-equal assertion and a JSON round-trip do not agree about it,
    // so it is normalised here rather than guarded against at every producer.
    return { amount: amount === 0 ? 0 : amount, currency };
}
export function zero(currency) {
    return money(0, currency);
}
/* -------------------------------------------------------------------- arithmetic */
/** Every binary operation goes through this; there is no second copy of the check. */
export function assertSameCurrency(a, b) {
    if (a.currency !== b.currency) {
        throw new DomainError('ValidationFailed', `Cannot mix ${a.currency} and ${b.currency} in one amount.`, { left: a.currency, right: b.currency });
    }
}
export function add(a, b) {
    assertSameCurrency(a, b);
    return money(a.amount + b.amount, a.currency);
}
export function subtract(a, b) {
    assertSameCurrency(a, b);
    return money(a.amount - b.amount, a.currency);
}
export function negate(value) {
    return money(-value.amount, value.currency);
}
/**
 * Adds a list. The currency is a parameter and not inferred, so summing an empty
 * list is a question with an answer rather than a crash.
 */
export function sum(values, currency) {
    let total = 0;
    for (const value of values) {
        if (value.currency !== currency) {
            throw new DomainError('ValidationFailed', `Cannot sum a ${value.currency} amount into a ${currency} total.`, { expected: currency, actual: value.currency });
        }
        total += value.amount;
    }
    return money(total, currency);
}
/**
 * Multiplies by a quantity that may be fractional — 6.0 hours, 2.5 hours, 4
 * articles — and rounds the result half away from zero to the minor unit.
 */
export function multiplyByQty(value, qty) {
    if (!Number.isFinite(qty)) {
        throw new DomainError('ValidationFailed', 'A quantity is a finite number.', {
            qty: String(qty),
        });
    }
    const product = value.amount * qty;
    if (product > Number.MAX_SAFE_INTEGER || product < -Number.MAX_SAFE_INTEGER) {
        throw new DomainError('ValidationFailed', 'That quantity takes the amount past MAX_SAFE_INTEGER.', { amount: value.amount, qty });
    }
    return money(roundHalfUp(product), value.currency);
}
/**
 * Divides by a quantity that may be fractional — 33 hours, 1.8 hours — and rounds
 * the result half away from zero to the minor unit. `multiplyByQty`'s inverse: an
 * effective rate (`readmodels/formulae.md` §Effective $/hour) is an amount divided
 * by hours worked, the one division this system does on a `Money` outside a
 * discount or a conversion.
 */
export function perUnit(value, qty) {
    if (!Number.isFinite(qty) || qty <= 0) {
        throw new DomainError('ValidationFailed', 'A quantity divided into an amount is greater than zero.', {
            qty: String(qty),
        });
    }
    // `qty` may itself be fractional (1.8 retainer hours), so this is `roundHalfUp`
    // over a plain division — `divideRoundHalfUp`'s exact-integer arithmetic assumes
    // a whole-number denominator, which a quantity of hours is not.
    return money(roundHalfUp(value.amount / qty), value.currency);
}
/**
 * Applies a rate in basis points, to the minor unit, half away from zero. Discounts
 * pass a negative rate, so `add(list, applyBps(list, -1500))` is the discounted
 * amount and the discount is a step you can show.
 */
export function applyBps(value, bps) {
    assertSafeInteger(bps, 'Basis points are a whole number — 1500 is 15%.', { bps: String(bps) });
    const scaled = value.amount * bps;
    assertSafeInteger(scaled, 'That rate takes the amount past MAX_SAFE_INTEGER.', {
        amount: value.amount,
        bps,
    });
    return money(divideRoundHalfUp(scaled, BPS_PER_WHOLE), value.currency);
}
/* --------------------------------------------------------------------- ordering */
export function isZero(value) {
    return value.amount === 0;
}
export function isNegative(value) {
    return value.amount < 0;
}
export function compare(a, b) {
    assertSameCurrency(a, b);
    if (a.amount < b.amount)
        return -1;
    if (a.amount > b.amount)
        return 1;
    return 0;
}
export function min(a, b) {
    return compare(a, b) <= 0 ? a : b;
}
export function max(a, b) {
    return compare(a, b) >= 0 ? a : b;
}
/* --------------------------------------------------------------------- rounding */
/**
 * To the nearest `unitMinor` minor units, half away from zero. `round10` and
 * `round5` are one-liners over this and there is no third rounding function.
 */
export function roundToUnit(value, unitMinor) {
    assertSafeInteger(unitMinor, 'A rounding unit is a whole number of minor units.', {
        unitMinor: String(unitMinor),
    });
    if (unitMinor < 1) {
        throw new DomainError('ValidationFailed', 'A rounding unit is at least one minor unit.', {
            unitMinor,
        });
    }
    return money(divideRoundHalfUp(value.amount, unitMinor) * unitMinor, value.currency);
}
/** To the nearest $10 — monthly retainers and project totals (`PRICING.md`). */
export function round10(value) {
    return roundToUnit(value, ROUND10_UNIT);
}
/** To the nearest $5 — extra unit prices (`PRICING.md`). */
export function round5(value) {
    return roundToUnit(value, ROUND5_UNIT);
}
/**
 * `amount × daysUsed / daysInMonth`, to the minor unit, half away from zero.
 *
 * `SERVICES.md` §Retainers: "A client who starts mid-month is invoiced once at
 * signup: the rest of that month prorated by days, and the first full month."
 * Golden: `prorateByDays($3,180.00, 28, 31) = $2,872.26` (`FES-2026-011`).
 */
export function prorateByDays(value, daysUsed, daysInMonth) {
    if (!DAYS_IN_A_MONTH.includes(daysInMonth)) {
        throw new DomainError('ValidationFailed', 'A month is 28, 29, 30 or 31 days long.', {
            daysInMonth: String(daysInMonth),
        });
    }
    assertSafeInteger(daysUsed, 'A day count is a whole number.', { daysUsed: String(daysUsed) });
    if (daysUsed < 0 || daysUsed > daysInMonth) {
        throw new DomainError('ValidationFailed', 'Days used fall inside the month.', {
            daysUsed,
            daysInMonth,
        });
    }
    const scaled = value.amount * daysUsed;
    assertSafeInteger(scaled, 'That amount is too large to prorate exactly.', {
        amount: value.amount,
        daysUsed,
    });
    return money(divideRoundHalfUp(scaled, daysInMonth), value.currency);
}
/* --------------------------------------------------------------- major units */
/**
 * How many minor units are in one major one. Both currencies here are two decimals.
 */
export const MINOR_UNITS_PER_MAJOR = 100;
/**
 * An outside system's decimal amount as this system's minor units.
 *
 * Session 15's one caller is Wise, which reports `2993.00` and `1842.60` as JSON
 * numbers (`PAYMENTS.md` §Wise). Everything in this repository is minor units, and
 * the conversion is here rather than at the gateway for two reasons: `Math.round` is
 * lint-banned outside this file and `dates.ts`, and the rounding is genuinely
 * necessary — `1842.60 * 100` is `184259.99999999997` in IEEE 754, and a truncation
 * would post `€1,842.59` against an invoice for `€1,842.60`.
 *
 * A string is accepted and preferred, because a provider that sends `"1842.60"` has
 * given us the exact value and `Number` is the only thing that would lose it.
 */
export function fromMajorUnits(value, currency) {
    const parsed = typeof value === 'string' ? Number(value) : value;
    if (!Number.isFinite(parsed)) {
        throw new DomainError('ValidationFailed', 'That is not an amount.', { value: String(value) });
    }
    return money(roundHalfUp(parsed * MINOR_UNITS_PER_MAJOR), currency);
}
/** The other way, for a provider that wants decimals back. */
export function toMajorUnits(value) {
    return value.amount / MINOR_UNITS_PER_MAJOR;
}
/* ------------------------------------------------------------------- conversion */
/**
 * Converts at the internal USD-per-EUR rate, to the minor unit, half away from zero.
 * Identity when the currencies already match.
 *
 * `.docs/00-index.md` §6: "Compute in USD to the cent, convert at the internal rate,
 * then round (`round10` / `round5`) **once**, in the billing currency." So this
 * returns an exact converted amount; the caller rounds after it.
 */
export function convert(value, rateUsdPerEur, to) {
    assertCurrency(value.currency);
    assertCurrency(to);
    if (!Number.isFinite(rateUsdPerEur) || rateUsdPerEur <= 0) {
        throw new DomainError('ValidationFailed', 'A USD-per-EUR rate is a positive number.', {
            rate: String(rateUsdPerEur),
        });
    }
    if (value.currency === to)
        return value;
    return value.currency === 'USD'
        ? money(roundHalfUp(value.amount / rateUsdPerEur), to)
        : money(roundHalfUp(value.amount * rateUsdPerEur), to);
}
/* ----------------------------------------------------------------------- splits */
/**
 * Splits a total into parts that sum back to it exactly, by largest remainder, with
 * ties going to the earlier part. Milestone schedules are the caller
 * (`SERVICES.md` §Projects: "milestones as percentages").
 *
 * Percentages are whole numbers summing to 100 — every schedule in `SERVICES.md`
 * and the mockups is (50/50, 20/30/30/20), and integers make the split exact
 * instead of approximately exact.
 */
export function splitByPercent(total, percents) {
    if (percents.length === 0) {
        throw new DomainError('ValidationFailed', 'A split needs at least one percentage.', {});
    }
    let totalPercent = 0;
    for (const percent of percents) {
        if (!Number.isSafeInteger(percent) || percent < 0 || percent > 100) {
            throw new DomainError('ValidationFailed', 'A milestone percentage is a whole number between 0 and 100.', { percent: String(percent) });
        }
        totalPercent += percent;
    }
    if (totalPercent !== 100) {
        throw new DomainError('ValidationFailed', 'Milestone percentages sum to 100.', {
            sum: totalPercent,
        });
    }
    const negative = total.amount < 0;
    const magnitude = negative ? -total.amount : total.amount;
    assertSafeInteger(magnitude * 100, 'That total is too large to split exactly.', {
        amount: total.amount,
    });
    const scaled = percents.map((percent) => magnitude * percent);
    const parts = scaled.map((value) => (value - (value % 100)) / 100);
    const leftover = magnitude - parts.reduce((running, part) => running + part, 0);
    const byRemainder = scaled.map((value, index) => ({ index, remainder: value % 100 }));
    byRemainder.sort((a, b) => b.remainder - a.remainder || a.index - b.index);
    const getsAnExtraUnit = new Set(byRemainder.slice(0, leftover).map((entry) => entry.index));
    return parts.map((part, index) => {
        const amount = part + (getsAnExtraUnit.has(index) ? 1 : 0);
        return money(negative ? -amount : amount, total.currency);
    });
}
/**
 * The only currency formatter in the repository. Goldens: `$3,180.00`, `$3,180`
 * (auto), `−$560.00` with a real minus sign, `€890.00`.
 */
export function formatMoney(value, locale = 'en-US', options = {}) {
    const whole = (options.showCents ?? 'always') === 'auto' && value.amount % MINOR_UNITS === 0;
    const digits = whole ? 0 : 2;
    return formatWithMinusSign(numberFormat(locale, {
        style: 'currency',
        currency: value.currency,
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    }), value.amount / MINOR_UNITS);
}
//# sourceMappingURL=money.js.map